import { useCallback, useEffect, useState } from "react";
import FeedbackTopHeader from "../../../newComponents/listings/top-listing-headers/FeedbackTopHeader";
import FeedbackCardItem from "./FeedbackCardItem";
import AddFeedbackForm from "./AddFeedbackForm";
import { PlatformService } from "@propertelligent/client-api";
import { infiniteScrollHook } from "../../../hooks/infiniteScrollHook";
import { UserLoginData } from "../../common/charts/Enums";
import SimpleSpinner from "../../loader";
import EmptyData from "../../common/EmptyData";
import { GetImages } from "../../../utils/GetImages";
import EmptyState from "../../../newComponents/emptystate";

// Permission support
import { usePermissions } from "../../../hooks/usePermissions";
import { PermissionGuard } from "../../../newComponents/PermissionGuard";
import NoAccess from "../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";

const FeedbackComponent = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("All");
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<any>(null);
  const [storedAggregates, setStoredAggregates] = useState<Record<string, number>>({});
  const [totalCount, setTotalCount] = useState(0);

  const check = usePermissions();
  const canWrite = check.canWrite("Feedback");

  const fullyQualifiedFilters = (() => {
    const filters: string[] = [];

    if (activeFilter !== "All") {
      filters.push(`FeedbackTypeId == ${activeFilter}`);
    }

    if (searchTerm) {
      filters.push(
        `FeedbackDescription.ToLower().Contains("${searchTerm.toLowerCase()}")`
      );
    }

    return filters.length ? filters.join(" && ") : null;
  })();

  const {
    rows,
    loading,
    fetchData,
    aggregateValues,
  } = infiniteScrollHook({
    httpFetchDataFn: (
      orgId: number,
      pageSize?: number,
      pageNumber?: number,
      skipValue?: number,
      filterEntityTypeId?: any,
      filterEntityIds?: any,
      filters?: string,
      sorts?: string,
      ...rest: any[]
    ) =>
      PlatformService.getApiFeedbackOrganizationlist(
        orgId,
        pageSize,
        pageNumber,
        skipValue,
        filterEntityTypeId,
        filterEntityIds,
        filters,
        sorts,
        ...rest
      ),
    FullyQualifiedFilters: fullyQualifiedFilters,
    pageSize: 10,
    FullyQualifiedSorts: "Created desc",
  });

  useEffect(() => {
    
    if (activeFilter === "All" && !searchTerm && aggregateValues) {
      setStoredAggregates(aggregateValues);

      const total = Object.values(aggregateValues as Record<string, number>).reduce(
        (sum, val) => sum + Number(val),
        0
      );

      setTotalCount(total);

    }
  }, [aggregateValues, activeFilter, searchTerm]);

  const handleEdit = (item: any) => {
    setSelectedFeedback(item);
    setModalOpen(true);
  };


const aggregateFilters =
  Object.keys(storedAggregates).length > 0
    ? storedAggregates
    : aggregateValues;

const handleAddClick = useCallback(() => {
  setSelectedFeedback(null);
  setModalOpen(true);
}, []);

const isEmpty = rows.length === 0 && !loading;

return (
  <PermissionGuard permission="Feedback" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
  <div className="content-wrapper container-fluid">
    <FeedbackTopHeader
      PageHeading="Feedback"
      Count={totalCount}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      activeFilter={activeFilter}
      setActiveFilter={setActiveFilter}
      aggregateValuesFilters={aggregateFilters}
      onAddClick={canWrite ? handleAddClick : undefined}
    />

    <div className="infinite-scroll-container">
      {isEmpty && (
        <EmptyState
          icon={GetImages.emptyStateFeedback}
          heading="No Feedback Found"
          isButton={false}
          isBorder
        />
      )}

      {rows.map((item: any) => (
        <FeedbackCardItem
          key={item.id}
          listingData={item}
          onEdit={() => handleEdit(item)}
        />
      ))}

      {loading && <SimpleSpinner />}
    </div>

    {isModalOpen && (
      <AddFeedbackForm
        isModalOpen={isModalOpen}
        setModalOpen={setModalOpen}
        editData={selectedFeedback}
        fetchData={() => fetchData(1)}
      />
    )}
  </div>
  </PermissionGuard>
);

};

export default FeedbackComponent;